import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  ChatBubbleLeftIcon,
  MagnifyingGlassIcon,
  PaperAirplaneIcon,
  EllipsisVerticalIcon,
  UserIcon,
  FaceSmileIcon,
  ArrowLeftIcon,
  MapPinIcon,
  ClockIcon,
  XCircleIcon,
  LockClosedIcon,
  ArrowTopRightOnSquareIcon,
  CalendarDaysIcon,
  BanknotesIcon
} from '@heroicons/react/24/outline';
import EmojiPicker, { EmojiClickData } from 'emoji-picker-react';
import TextareaAutosize from 'react-textarea-autosize';
import Avatar from '../components/common/Avatar';
import Button from '../components/common/Button';
import ConfirmationModal from '../components/common/ConfirmationModal';
import StackedAvatars from '../components/collaboration/StackedAvatars';
import CancellationReasonModal from '../components/collaboration/CancellationReasonModal';
import CollaborationCompletionModal from '../components/collaboration/CollaborationCompletionModal';
import CreateCollaborationModal from '../components/collaboration/CreateCollaborationModal';
import { differenceInDays, differenceInHours, differenceInMinutes, isAfter } from 'date-fns';
import { formatDistanceToNow } from 'date-fns';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../hooks/redux';
import { Menu, Transition, Dialog } from '@headlessui/react';
import { Fragment } from 'react';
import { userAPI, crmAPI } from '../services/api';
import { Conversation } from '../types';
import { 
  fetchConversations, 
  fetchMessages, 
  sendMessage as sendMessageAction,
  setCurrentConversation,
  clearMessages,
  markAsRead,
  fetchMessageRequests,
  handleMessageRequest,
  updateConversation,
  deleteConversation
} from '../store/slices/messagesSlice';
import toast from 'react-hot-toast';
import useWebSocket from '../hooks/useWebSocket';
import CollaborationDetailModal from '../components/collaboration/CollaborationDetailModal';
import { collaborationAPI } from '../services/api';
import { Collaboration, Portfolio, Message } from '../types';
import { formatCurrency } from '../utils/format';

const MessagesPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const renderTextWithLinks = (text: string, isOwn: boolean) => {
    if (!text) return null;
    const urlRegex = /(https?:\/\/[^\s]+|\/(?:gallery|invoice|crm\/contract)\/[a-zA-Z0-9-]+(?:\?[^\s]*)?)/g;
    
    const parts = text.split(urlRegex);
    if (parts.length === 1) return text;
    
    return parts.map((part, idx) => {
      if (part.match(urlRegex)) {
        const href = part.startsWith('/') ? window.location.origin + part : part;
        
        let label = part;
        if (part.includes('/gallery/')) {
          label = "View Gallery";
        } else if (part.includes('/invoice/')) {
          label = "View Invoice";
        } else if (part.includes('/crm/contract/')) {
          label = "View Contract";
        } else {
          try {
            const urlObj = new URL(href);
            label = `Open ${urlObj.hostname}`;
          } catch (e) {
            label = "Open Link";
          }
        }

        return (
          <a 
            key={idx} 
            href={href} 
            target="_blank" 
            rel="noopener noreferrer" 
            className={`underline font-extrabold inline-flex items-center gap-1 mx-1.5 transition-all hover:scale-[1.01] ${
              isOwn 
                ? 'text-teal-200 hover:text-white' 
                : 'text-teal-600 hover:text-teal-800 dark:text-teal-400 dark:hover:text-teal-300'
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            {label}
            <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5 inline-block align-middle" />
          </a>
        );
      }
      return part;
    });
  };
  
  const [viewingCollaboration, setViewingCollaboration] = useState<Collaboration | null>(null);
  const [loadingItem, setLoadingItem] = useState(false);

  const handleItemClick = async (type: string, data: any) => {
    console.log("handleItemClick triggered - type:", type, "data:", data, "currentUser:", currentUser);
    if (type === 'collaboration') {
        if (data.id) {
            setLoadingItem(true);
            try {
                const response = await collaborationAPI.getCollaboration(data.id);
                if (response.data.success && response.data.data) {
                    setViewingCollaboration(response.data.data);
                } else {
                    navigate(`/collaborations/${data.id}`);
                }
            } catch (error) {
                console.error("Failed to fetch collaboration", error);
                navigate(`/collaborations/${data.id}`);
            } finally {
                setLoadingItem(false);
            }
        }
    } else if (type === 'portfolio') {
        if (data.id && data.user_id) {
             navigate(`/profile/${data.user_id}/portfolio/${data.id}`);
        }
    } else if (type === 'opportunity') {
        navigate(`/opportunities/${data.id}`);
    } else if (type === 'project_card') {
        console.log("Matching project_card condition! owner_id:", data.owner_id, "currentUserId:", currentUser?.id);
        if (currentUser?.id === data.owner_id) {
            console.log("Navigating to CRM projects tab with project ID:", data.crm_project_id);
            navigate('/crm', { 
                state: { 
                    activeTab: 'projects',
                    selectedProjectId: data.crm_project_id
                } 
            });
        } else {
            console.log("Navigating as lead/client. user_type:", currentUser?.user_type, "project ID:", data.crm_project_id);
            if (currentUser?.user_type === 'hirer') {
                navigate(`/profile/${currentUser.id}/my-projects`, {
                    state: {
                        selectedProjectId: data.crm_project_id
                    }
                });
            } else {
                navigate('/crm', { 
                    state: { 
                        activeTab: 'bookings',
                        selectedProjectId: data.crm_project_id
                    } 
                });
            }
        }
    }
  };
  const { connectionStatus } = useWebSocket();
  
  
  // Get state from Redux
  const { 
    conversations, 
    currentConversation, 
    messages, 
    loading: loadingConversations, 
    loadingMessages,
    currentPage,
    hasMore,
    conversationsPage,
    hasMoreConversations,
    messageRequests,
    loadingRequests
  } = useAppSelector((state) => state.messages);
  
  const { user: currentUser } = useAppSelector((state) => state.auth);
  
  // Local state for UI
  const [searchQuery, setSearchQuery] = useState('');
  const [newMessage, setNewMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [activeTab, setActiveTab] = useState<'messages' | 'requests'>('messages');
  const [collabFilter, setCollabFilter] = useState<'all' | 'active' | 'past'>('active');

  const handleCollabFilterChange = (filter: 'all' | 'active' | 'past') => {
    setCollabFilter(filter);
    dispatch(fetchConversations({ page: 1, collabFilter: filter }));
  };

  // Report Modal State
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [reportDescription, setReportDescription] = useState('');
  const [isSubmittingReport, setIsSubmittingReport] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [unreadDividerId, setUnreadDividerId] = useState<string | null>(null);
  const hasInitialUnreadCheck = useRef(false);
  
  // Modal State
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [showUnblockModal, setShowUnblockModal] = useState(false);
  const [blockActionLoading, setBlockActionLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  
  // Refs
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messageInputRef = useRef<HTMLTextAreaElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const [prevScrollHeight, setPrevScrollHeight] = useState(0);

  // Lifecycle Modal State
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [showCompleteConfirm, setShowCompleteConfirm] = useState(false);
  const [showCancellationModal, setShowCancellationModal] = useState(false);
  const [showCreateCollabModal, setShowCreateCollabModal] = useState(false);
  const [requestToProcess, setRequestToProcess] = useState<any>(null);
  const [collabRequest, setCollabRequest] = useState<any>(null);

  // Countdown State
  const [timeLeft, setTimeLeft] = useState<string | null>(null);

  // CRM States
  const [crmLeadDetails, setCrmLeadDetails] = useState<any>(null);
  const [crmProjectDetails, setCrmProjectDetails] = useState<any>(null);
  const [loadingCrm, setLoadingCrm] = useState(false);

  useEffect(() => {
    const fetchCrmDetails = async () => {
      if (!currentConversation) {
        setCrmLeadDetails(null);
        setCrmProjectDetails(null);
        return;
      }

      if (currentConversation.crm_lead_id) {
        setLoadingCrm(true);
        try {
          const response = await crmAPI.getPortalLead(currentConversation.crm_lead_id);
          if (response.data?.success && response.data?.data) {
            setCrmLeadDetails(response.data.data.lead || response.data.data);
          }
        } catch (error) {
          console.error("Failed to fetch CRM lead details", error);
        } finally {
          setLoadingCrm(false);
        }
      } else if (currentConversation.crm_project_id) {
        setLoadingCrm(true);
        try {
          const response = await crmAPI.getProject(currentConversation.crm_project_id);
          if (response.data?.success && response.data?.data) {
            setCrmProjectDetails(response.data.data.project || response.data.data);
          }
        } catch (error) {
          console.error("Failed to fetch CRM project details", error);
        } finally {
          setLoadingCrm(false);
        }
      } else {
        setCrmLeadDetails(null);
        setCrmProjectDetails(null);
      }
    };

    fetchCrmDetails();
  }, [currentConversation]);

  useEffect(() => {
    if (currentConversation?.closing_at) {
      const calculateTimeLeft = () => {
        const now = new Date();
        const end = new Date(currentConversation.closing_at!);
        if (isAfter(now, end)) {
          setTimeLeft('Closed');
          return true;
        }

        const days = differenceInDays(end, now);
        const hours = differenceInHours(end, now) % 24;
        const mins = differenceInMinutes(end, now) % 60;
        setTimeLeft(`${days}d ${hours}h ${mins}m`);
        return false;
      };

      // Trigger immediately
      const isExpired = calculateTimeLeft();
      if (isExpired) return;

      const timer = setInterval(() => {
        if (calculateTimeLeft()) {
          clearInterval(timer);
        }
      }, 60000);

      return () => clearInterval(timer);
    } else {
      setTimeLeft(null);
    }
  }, [currentConversation?.closing_at]);

  useEffect(() => {
    const fetchCollabRequest = async () => {
      if (currentConversation?.collaboration_request_id) {
        try {
          const response = await collaborationAPI.getCollaborationRequest(currentConversation.collaboration_request_id);
          setCollabRequest(response.data.data.request);
        } catch (error) {
          console.error("Failed to fetch collaboration request details", error);
          setCollabRequest(null);
        }
      } else {
        setCollabRequest(null);
      }
    };
    fetchCollabRequest();
  }, [currentConversation?.collaboration_request_id]);

  // Load conversations on mount
  useEffect(() => {
    dispatch(fetchConversations({ page: 1 }));
    dispatch(fetchMessageRequests({ page: 1 }));
    
    // Cleanup: Clear current conversation when leaving the page
    return () => {
      dispatch(clearMessages());
    };
  }, [dispatch]);

  // Blocked Users State
  const [blockedUserIds, setBlockedUserIds] = useState<Set<string>>(new Set());

  // Fetch blocked users on mount
  useEffect(() => {
    const fetchBlockedUsers = async () => {
      try {
        const users = await userAPI.getBlockedUsers();
        if (Array.isArray(users)) {
          setBlockedUserIds(new Set(users.map((u: any) => u.id)));
        }
      } catch (error) {
        console.error('Failed to fetch blocked users', error);
      }
    };
    fetchBlockedUsers();
  }, []);

  // Handle opening conversation from notification
  useEffect(() => {
    if (location.state?.openConversationId && location.state?.fromNotification) {
      const conversationId = location.state.openConversationId;
      
      if (conversations.length === 0) {
        dispatch(fetchConversations({ page: 1 })).then(() => {
          openConversationById(conversationId);
        });
      } else {
        openConversationById(conversationId);
      }
      
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, conversations, dispatch]);

  const openConversationById = (conversationId: string) => {
    const conversation = conversations.find(conv => conv.conversation_id === conversationId);
    if (conversation) {
      openConversation(conversation);
      dispatch(fetchMessages({ conversation_id: conversationId, page: 1 }));
    } else {
      console.warn('Conversation not found in list:', conversationId);
      toast.error('Conversation not found');
    }
  };

  // Handle opening specific conversation or starting new chat from navigation
  useEffect(() => {
    if (location.state?.openConversationWith) {
      if (loadingConversations) return; // Wait until conversations finish loading
      
      const targetUserId = location.state.openConversationWith;
      const collaboration_request_id = location.state.collaboration_request_id;
      const crm_lead_id = location.state.crm_lead_id;
      const crm_project_id = location.state.crm_project_id;
      const userName = location.state.userName;
      const userAvatar = location.state.userAvatar;
      const userType = location.state.userType;
      const prefilledMessage = location.state.prefilledMessage;
      
      const isMatch = currentConversation?.other_user?.id === targetUserId && 
        (!collaboration_request_id || currentConversation?.collaboration_request_id === collaboration_request_id) &&
        (!crm_lead_id || currentConversation?.crm_lead_id === crm_lead_id) &&
        (!crm_project_id || currentConversation?.crm_project_id === crm_project_id);

      if (isMatch) {
        if (prefilledMessage && !newMessage) {
          setNewMessage(prefilledMessage);
        }
        navigate(location.pathname, { replace: true, state: {} });
        return;
      }

      const existingConversation = conversations.find(conv => {
        if (!conv.other_user || conv.other_user.id !== targetUserId) return false;
        
        if (collaboration_request_id && conv.collaboration_request_id === collaboration_request_id) return true;
        if (crm_project_id && conv.crm_project_id === crm_project_id) return true;
        if (crm_lead_id && conv.crm_lead_id === crm_lead_id) return true;
        
        if (!collaboration_request_id && !crm_project_id && !crm_lead_id) {
          return !conv.collaboration_request_id && !conv.crm_project_id && !conv.crm_lead_id;
        }
        return false;
      }) || messageRequests.find(conv => {
        if (!conv.other_user || conv.other_user.id !== targetUserId) return false;
        
        if (collaboration_request_id && conv.collaboration_request_id === collaboration_request_id) return true;
        if (crm_project_id && conv.crm_project_id === crm_project_id) return true;
        if (crm_lead_id && conv.crm_lead_id === crm_lead_id) return true;
        
        if (!collaboration_request_id && !crm_project_id && !crm_lead_id) {
          return !conv.collaboration_request_id && !conv.crm_project_id && !conv.crm_lead_id;
        }
        return false;
      });
      
      if (existingConversation) {
        openConversation(existingConversation);
      } else if (userName && userType) {
        startNewConversation({
          id: targetUserId,
          name: userName,
          avatar: userAvatar,
          user_type: userType,
          collaboration_request_id: collaboration_request_id,
          crm_lead_id: crm_lead_id,
          crm_project_id: crm_project_id
        });
      }
      
      if (prefilledMessage) {
        setNewMessage(prefilledMessage);
      }
      
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [conversations, loadingConversations, location.state, currentConversation]);

  // Handle scroll for pagination
  const handleScroll = () => {
    const container = messagesContainerRef.current;
    if (!container) return;

    if (container.scrollTop < 10 && hasMore && !loadingMessages) {
      // console.log('Fetching older messages...');
      setPrevScrollHeight(container.scrollHeight);
      if (currentConversation) {
        dispatch(fetchMessages({ 
          conversation_id: currentConversation.conversation_id, 
          page: currentPage + 1 
        }));
      }
    }
  };

  // Restore scroll position after loading older messages
  useLayoutEffect(() => {
    const container = messagesContainerRef.current;
    if (container && prevScrollHeight > 0 && !loadingMessages) {
      const newScrollHeight = container.scrollHeight;
      const heightDifference = newScrollHeight - prevScrollHeight;
      container.scrollTop = heightDifference;
      setPrevScrollHeight(0);
    }
  }, [messages, loadingMessages, prevScrollHeight]);

  // Auto-scroll to bottom ONLY for new messages (page 1) or initial load
  useEffect(() => {
    if (currentPage === 1 && !loadingMessages && prevScrollHeight === 0) {
      scrollToBottom('auto');
    }
  }, [messages, currentPage, loadingMessages, prevScrollHeight]);

  // Set unread divider when messages are loaded
  useEffect(() => {
    if (!loadingMessages && messages.length > 0 && !hasInitialUnreadCheck.current) {
      const firstUnread = messages.find(m => !m.is_read && m.sender_id !== currentUser?.id);
      if (firstUnread) {
        setUnreadDividerId(firstUnread.id);
      }
      hasInitialUnreadCheck.current = true;
    }
  }, [messages, loadingMessages, currentUser?.id]);

  // Automatically mark new messages as read if we are viewing the conversation
  useEffect(() => {
    if (currentConversation && messages.length > 0 && currentUser) {
      const lastMessage = messages[messages.length - 1];
      if (lastMessage && !lastMessage.is_read && lastMessage.sender_id !== currentUser.id) {
        dispatch(markAsRead(currentConversation.conversation_id));
      }
    }
  }, [messages, currentConversation, currentUser, dispatch]);

  // Focus message input when conversation is selected
  useEffect(() => {
    if (currentConversation && messageInputRef.current) {
      messageInputRef.current.focus();
    }
  }, [currentConversation]);

  const scrollToBottom = (behavior: ScrollBehavior = 'smooth') => {
    messagesEndRef.current?.scrollIntoView({ behavior });
  };

  const openConversation = async (conversation: Conversation) => {
    dispatch(clearMessages());
    setUnreadDividerId(null);
    hasInitialUnreadCheck.current = false;
    dispatch(setCurrentConversation(conversation));
    dispatch(fetchMessages({conversation_id: conversation.conversation_id}));
    
    try {
      await dispatch(markAsRead(conversation.conversation_id)).unwrap();
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  };

  // Function to start a new conversation
  const startNewConversation = (user: {
    id: string;
    name: string;
    avatar?: string;
    user_type: string;
    collaboration_request_id?: string;
    crm_lead_id?: string;
    crm_project_id?: string;
  }) => {
    const tempConversation: Conversation = {
      conversation_id: `temp-${user.id}`,
      collaboration_request_id: user.collaboration_request_id,
      crm_lead_id: user.crm_lead_id,
      crm_project_id: user.crm_project_id,
      other_user: {
        id: user.id,
        email: '',
        first_name: user.name.split(' ')[0] || '',
        last_name: user.name.split(' ').slice(1).join(' ') || '',
        user_type: user.user_type as 'creator' | 'model',
        skills: [],
        experience: 'beginner',
        hourly_rate: 0,
        is_available: false,
        is_profile_complete: false,
        completion_score: 0,
        rating: 0,
        review_count: 0,
        project_count: 0,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        profile_pic: user.avatar,
        followers_count: 0,
        following_count: 0,
        collaborations_count: 0,
        collaborators_count: 0,
        collaboration_count: 0,
        has_portfolio: false,
        portfolio_items_count: 0,
        specializations: [],
        display_email: false,
        display_phone_number: false,
        do_i_follow: false,
        is_new_follow: false      
      },
      last_message: '',
      last_message_at: new Date().toISOString()
    };

    dispatch(clearMessages());
    dispatch(setCurrentConversation(tempConversation));
  };

  const sendMessageHandler = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim() || isSending || !currentConversation || !currentUser) return;

    if (currentConversation.is_blocked_by_me || (currentConversation.other_user?.id && blockedUserIds.has(currentConversation.other_user.id))) {
      toast.error('You have blocked this user. Unblock to send messages.');
      return;
    }
    if (currentConversation.is_blocked_by_them) {
      toast.error('You cannot send messages to this user.');
      return;
    }

    setIsSending(true);
    setUnreadDividerId(null);
    const messageContent = newMessage.trim();
    setNewMessage('');
    
    try {
      const messageData = {
        recipient_id: currentConversation.other_user.id,
        content: messageContent,
        conversation_id: currentConversation.conversation_id.startsWith('temp-') 
          ? undefined 
          : currentConversation.conversation_id,
        collaboration_request_id: currentConversation.collaboration_request_id,
        crm_lead_id: currentConversation.crm_lead_id,
        crm_project_id: currentConversation.crm_project_id,
      };
      
      const response = await dispatch(sendMessageAction(messageData)).unwrap();
      
      if (currentConversation.conversation_id && !currentConversation.conversation_id.startsWith('temp-')) {
        dispatch(markAsRead(currentConversation.conversation_id));
      }
      
      if (currentConversation.conversation_id.startsWith('temp-')) {
        const newConversation = {
          ...currentConversation,
          conversation_id: response.conversation?.id || response.conversation_id,
          last_message: messageContent,
          last_message_at: response.created_at || new Date().toISOString()
        };
        
        dispatch(setCurrentConversation(newConversation));
        dispatch(fetchConversations({ page: 1 }));
      }
      
    } catch (error) {
      // console.log('Failed to send message:', error);
      toast.error('Failed to send message');
      setNewMessage(messageContent);
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessageHandler(e as any);
    }
  };

  const onEmojiClick = (emojiData: EmojiClickData) => {
    setNewMessage((prev) => prev + emojiData.emoji);
  };

  const handleBlockUser = async () => {
    if (!currentConversation?.other_user?.id) return;
    setBlockActionLoading(true);
    try {
      await userAPI.blockUser(currentConversation.other_user.id);
      toast.success('User blocked successfully');
      
      // Update local blocked state
      setBlockedUserIds(prev => new Set(prev).add(currentConversation.other_user.id));
      
      if (currentConversation) {
        dispatch(setCurrentConversation({
          ...currentConversation,
          is_blocked_by_me: true
        }));
        dispatch(updateConversation({
          conversation_id: currentConversation.conversation_id,
          is_blocked_by_me: true
        }));
      }
      setShowBlockModal(false);
    } catch (error) {
      toast.error('Failed to block user');
    } finally {
      setBlockActionLoading(false);
    }
  };

  const handleUnblockUser = async () => {
    if (!currentConversation?.other_user?.id) return;
    setBlockActionLoading(true);
    try {
      await userAPI.unblockUser(currentConversation.other_user.id);
      toast.success('User unblocked successfully');
      
      // Update local blocked state
      setBlockedUserIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(currentConversation.other_user.id);
        return newSet;
      });

      if (currentConversation) {
        dispatch(setCurrentConversation({
          ...currentConversation,
          is_blocked_by_me: false
        }));
        dispatch(updateConversation({
          conversation_id: currentConversation.conversation_id,
          is_blocked_by_me: false
        }));
      }
      setShowUnblockModal(false);
    } catch (error) {
      toast.error('Failed to unblock user');
    } finally {
      setBlockActionLoading(false);
    }
  };

  const handleReportUser = async () => {
    if (!currentConversation?.other_user?.id || !reportReason) return;
    
    setIsSubmittingReport(true);
    try {
      await userAPI.reportUser({
        reported_id: currentConversation.other_user.id,
        reason: reportReason,
        description: reportDescription
      });
      toast.success('Report submitted successfully');
      setIsReportModalOpen(false);
      setReportReason('');
      setReportDescription('');
    } catch (error) {
      toast.error('Failed to submit report');
    } finally {
      setIsSubmittingReport(false);
    }
  };

  const formatMessageTime = (timestamp: string) => {
    if (!timestamp) return '';
    
    try {
      const date = new Date(timestamp);
      const now = new Date();
      const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);
      
      if (diffInHours < 24) {
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      } else if (diffInHours < 168) {
        return date.toLocaleDateString([], { weekday: 'short' });
      } else {
        return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
      }
    } catch (error) {
      return '';
    }
  };

  const handleAcceptRequest = async () => {
    if (!currentConversation) return;
    try {
      await dispatch(handleMessageRequest({ 
        conversationId: currentConversation.conversation_id, 
        action: 'accept' 
      })).unwrap();
      toast.success('Request accepted');
    } catch (error) {
      toast.error('Failed to accept request');
    }
  };

  const handleRejectRequest = async () => {
    if (!currentConversation) return;
    try {
      await dispatch(handleMessageRequest({ 
        conversationId: currentConversation.conversation_id, 
        action: 'reject' 
      })).unwrap();
      toast.success('Request rejected');
      dispatch(clearMessages());
    } catch (error) {
      toast.error('Failed to reject request');
    }
  };

  // Safe filter function for conversations
  const filteredConversations = Array.isArray(conversations) 
    ? conversations.filter(conv => {
        if (!conv.other_user) return false;
        const fullName = `${conv.other_user.first_name || ''} ${conv.other_user.last_name || ''}`.toLowerCase();
        return fullName.includes(searchQuery.toLowerCase());
      })
    : [];

  const filteredRequests = Array.isArray(messageRequests)
    ? messageRequests.filter(conv => {
        if (!conv.other_user) return false;
        const fullName = `${conv.other_user.first_name || ''} ${conv.other_user.last_name || ''}`.toLowerCase();
        return fullName.includes(searchQuery.toLowerCase());
      })
    : [];

  const formatLastMessage = (message: string) => {
    if (!message) return 'No messages yet';
    try {
      // Check if it looks like JSON before parsing to avoid unnecessary try/catch overhead
      if (message.trim().startsWith('{') && message.trim().endsWith('}')) {
        const parsed = JSON.parse(message);
        if (parsed && typeof parsed === 'object' && parsed.type) {
          switch (parsed.type) {
            case 'profile':
              return 'Shared a profile';
            case 'portfolio':
              return 'Shared a portfolio';
            case 'collaboration':
              return 'Shared a collaboration';
            case 'opportunity':
              return 'Shared an opportunity';
            default:
              return 'Shared content';
          }
        }
      }
      return message;
    } catch (e) {
      return message;
    }
  };

  const handleRevokeComplete = async () => {
    if (!currentConversation?.collaboration_request_id) return;
    try {
      await collaborationAPI.revokeCompleteCollaborationRequest(currentConversation.collaboration_request_id);
      toast.success('Completion mark revoked successfully!');
      // Re-fetch request details
      const response = await collaborationAPI.getCollaborationRequest(currentConversation.collaboration_request_id);
      setCollabRequest(response.data.data.request);
    } catch (error) {
      toast.error('Failed to revoke completion mark');
    }
  };

  const handleDeleteConversation = async () => {
    if (!currentConversation) return;
    try {
      await dispatch(deleteConversation(currentConversation.conversation_id)).unwrap();
      toast.success('Conversation deleted');
      setShowDeleteModal(false);
    } catch (error) {
      toast.error('Failed to delete conversation');
    }
  };

  const isCurrentClosed = !!currentConversation?.closing_at || currentConversation?.is_closed;
  const isCurrentExpired = currentConversation?.is_closed || (currentConversation?.closing_at && isAfter(new Date(), new Date(currentConversation.closing_at!)));

  const displayList = activeTab === 'messages' 
    ? filteredConversations.filter(conv => {
        if (collabFilter === 'all') return true;
        
        const isTerminal = conv.collaboration_request_status 
          ? (conv.collaboration_request_status === 'completed' || conv.collaboration_request_status === 'cancelled' || conv.collaboration_request_status === 'declined')
          : conv.crm_project_status
            ? (conv.crm_project_status === 'completed' || conv.crm_project_status === 'cancelled')
            : (conv.is_closed || (conv.closing_at && isAfter(new Date(), new Date(conv.closing_at!))));
        
        if (collabFilter === 'active') {
          return !isTerminal;
        } else {
          return isTerminal;
        }
      })
    : filteredRequests;

  const isLoadingList = activeTab === 'messages' ? loadingConversations : loadingRequests;

    return (
        <div className="h-full flex bg-white dark:bg-gray-900 overflow-hidden relative">
          {/* Connection Status Indicator */}
          <div className="absolute top-0 left-0 right-0 z-50 pointer-events-none">
             {connectionStatus !== 'connected' && (
                <div className={`mx-auto w-fit mt-2 px-4 py-1.5 rounded-full text-xs font-medium shadow-sm backdrop-blur-md ${
                  connectionStatus === 'connecting' ? 'bg-yellow-50/90 text-yellow-700 border border-yellow-200' :
                  connectionStatus === 'reconnecting' ? 'bg-blue-50/90 text-blue-700 border border-blue-200' :
                  'bg-red-50/90 text-red-700 border border-red-200'
                }`}>
                  {connectionStatus === 'connecting' && 'Connecting...'}
                  {connectionStatus === 'reconnecting' && 'Reconnecting...'}
                  {connectionStatus === 'disconnected' && 'Disconnected'}
                </div>
              )}
          </div>

          {/* Sidebar */}
          <div className={`${
            currentConversation ? 'hidden md:flex' : 'flex'
          } md:w-96 w-full flex-col border-r border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 z-10 transition-all duration-300`}>
            
            {/* Header */}
            <div className="p-4 md:p-5 border-b border-gray-100 dark:border-gray-800 bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm sticky top-0 z-10">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 tracking-tight">Messages</h1>
              
              {/* Tabs */}
              <div className="flex space-x-4 mb-4 border-b border-gray-100 dark:border-gray-800">
                <button
                  onClick={() => setActiveTab('messages')}
                  className={`pb-2 text-sm font-medium transition-colors ${
                    activeTab === 'messages' 
                      ? 'text-teal-600 border-b-2 border-teal-600' 
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                  }`}
                >
                  Messages
                </button>
                <button
                  onClick={() => {
                    setActiveTab('requests');
                    dispatch(fetchMessageRequests({ page: 1 }));
                  }}
                  className={`pb-2 text-sm font-medium transition-colors ${
                    activeTab === 'requests' 
                      ? 'text-teal-600 border-b-2 border-teal-600' 
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                  }`}
                >
                  Requests
                  {messageRequests.length > 0 && (
                    <span className="ml-2 bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full">
                      {messageRequests.length}
                    </span>
                  )}
                </button>
              </div>

              {/* Collaboration Filters - Shown only in Messages tab */}
              {activeTab === 'messages' && (
                <div className="flex items-center space-x-2 mb-4 overflow-x-auto pb-1 scrollbar-none">
                  <button
                    onClick={() => handleCollabFilterChange('all')}
                    className={`whitespace-now-nowrap px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${
                      collabFilter === 'all'
                        ? 'bg-teal-600 text-white shadow-sm'
                        : 'bg-gray-100 dark:bg-gray-800 text-gray-500 hover:bg-gray-200 dark:hover:bg-gray-700'
                    }`}
                  >
                    All Chats
                  </button>
                  <button
                    onClick={() => handleCollabFilterChange('active')}
                    className={`whitespace-now-nowrap px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${
                      collabFilter === 'active'
                        ? 'bg-teal-600 text-white shadow-sm'
                        : 'bg-gray-100 dark:bg-gray-800 text-gray-500 hover:bg-gray-200 dark:hover:bg-gray-700'
                    }`}
                  >
                    Active Collaborations
                  </button>
                  <button
                    onClick={() => handleCollabFilterChange('past')}
                    className={`whitespace-now-nowrap px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${
                      collabFilter === 'past'
                        ? 'bg-teal-600 text-white shadow-sm'
                        : 'bg-gray-100 dark:bg-gray-800 text-gray-500 hover:bg-gray-200 dark:hover:bg-gray-700'
                    }`}
                  >
                    Past Collaborations
                  </button>
                </div>
              )}

              <div className="relative group">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 group-focus-within:text-teal-500 transition-colors" />
                <input
                  type="text"
                  placeholder="Search conversations..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-gray-800 border-none rounded-xl text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-teal-500/20 focus:bg-white dark:focus:bg-gray-700 transition-all shadow-sm"
                />
              </div>
            </div>
          
            {/* Conversations List */}
            <div 
                className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-200 dark:scrollbar-thumb-gray-700 hover:scrollbar-thumb-gray-300 dark:hover:scrollbar-thumb-gray-600"
                onScroll={(e) => {
                    const target = e.currentTarget;
                    if (
                        target.scrollHeight - target.scrollTop <= target.clientHeight + 400 &&
                        hasMoreConversations &&
                        !loadingConversations &&
                        activeTab === 'messages'
                    ) {
                        dispatch(fetchConversations({ page: conversationsPage + 1, collabFilter }));
                    }
                }}
            >
              {isLoadingList && conversationsPage === 1 ? (
                <div className="p-4 space-y-4">
                  {[...Array(5)].map((_, i) => (
                    <div key={i} className="animate-pulse flex items-center space-x-4 p-2">
                      <div className="w-12 h-12 bg-gray-100 dark:bg-gray-800 rounded-full"></div>
                      <div className="flex-1 space-y-2">
                        <div className="h-4 bg-gray-100 dark:bg-gray-800 rounded w-3/4"></div>
                        <div className="h-3 bg-gray-100 dark:bg-gray-800 rounded w-1/2"></div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : displayList.length > 0 ? (
                <div className="p-2 space-y-1">
                  {displayList.map((conversation) => {
                    const isActive = currentConversation?.conversation_id === conversation.conversation_id;
                    const fullName = `${conversation.other_user?.first_name || ''} ${conversation.other_user?.last_name || ''}`;
                    
                    return (
                      <button
                        key={conversation.conversation_id}
                        onClick={() => {
                            if(currentConversation?.conversation_id !== conversation.conversation_id){
                                openConversation(conversation);
                            }
                        }}
                        className={`w-full p-3 rounded-xl text-left transition-all duration-200 group border-b border-gray-50 dark:border-gray-800/50 ${
                          isActive 
                            ? 'bg-teal-50 dark:bg-teal-900/20 shadow-sm ring-1 ring-teal-100 dark:ring-teal-800' 
                            : 'hover:bg-gray-50 dark:hover:bg-gray-800/50'
                        }`}
                      >
                        <div className="flex items-start space-x-3">
                          <div 
                            className="relative flex-shrink-0 cursor-pointer z-10"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (conversation.other_user?.id) {
                                navigate(`/profile/${conversation.other_user.id}`);
                              }
                            }}
                          >
                            {conversation.collaboration_request_id ? (
                              <StackedAvatars 
                                avatars={[
                                 conversation.other_user?.profile_pic_signed || '',
                                 currentUser?.profile_pic_signed || ''
                                ]}
                                names={[
                                  fullName,
                                  `${currentUser?.first_name || ''} ${currentUser?.last_name || ''}`
                                ]}
                                size="lg"
                              />
                            ) : (
                              <Avatar 
                                src={conversation.other_user?.profile_pic_signed} 
                                alt={fullName}
                                size="lg"
                                className={`w-12 h-12 ring-2 ${isActive ? 'ring-teal-200 dark:ring-teal-700' : 'ring-transparent group-hover:ring-gray-100 dark:group-hover:ring-gray-700'} transition-all`}
                                isDeleted={!!conversation.other_user?.deleted_at || !!conversation.other_user?.deletion_scheduled_at}
                              />
                            )}
                            {conversation.other_user?.is_online && !conversation.collaboration_request_id && (
                              <div className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 bg-green-500 border-2 border-white dark:border-gray-900 rounded-full shadow-sm"></div>
                            )}
                          </div>
                          
                          <div className="flex-1 min-w-0 pt-0.5">
                            <div className="flex items-center justify-between mb-0.5">
                              <h3 className={`font-semibold truncate text-sm ${isActive ? 'text-teal-900 dark:text-white' : 'text-gray-900 dark:text-white'}`}>
                                {conversation.subject ? `${conversation.subject} - ${fullName}` : fullName}
                              </h3>
                              <span className={`text-xs flex-shrink-0 ml-2 ${isActive ? 'text-teal-600/70 dark:text-teal-400' : 'text-gray-400 dark:text-gray-500'}`}>
                                {conversation.last_message_at && formatMessageTime(conversation.last_message_at)}
                              </span>
                            </div>
                            
                            <div className="flex items-center justify-between">
                              <p className={`text-sm truncate pr-2 ${
                                conversation.unread_count && conversation.unread_count > 0 
                                  ? 'font-bold text-gray-900 dark:text-white' 
                                  : isActive ? 'text-teal-700/80 dark:text-teal-300' : 'text-gray-500 dark:text-gray-400'
                              }`}>
                                {formatLastMessage(conversation.last_message)}
                              </p>
                              {conversation.unread_count && conversation.unread_count > 0 ? (
                                <span className="flex-shrink-0 min-w-[1.25rem] h-5 flex items-center justify-center bg-teal-500 text-white text-[10px] font-bold px-1.5 rounded-full shadow-sm shadow-teal-200">
                                  {conversation.unread_count}
                                </span>
                              ) : null}
                            </div>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-64 text-center px-6">
                  <div className="w-16 h-16 bg-gray-50 dark:bg-gray-800 rounded-full flex items-center justify-center mb-4">
                    <ChatBubbleLeftIcon className="h-8 w-8 text-gray-300 dark:text-gray-600" />
                  </div>
                  <h3 className="text-gray-900 dark:text-white font-medium mb-1">No {activeTab === 'messages' ? 'conversations' : 'requests'}</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {searchQuery ? 'No matches found.' : activeTab === 'messages' ? 'Start connecting with creators!' : 'You have no pending requests.'}
                  </p>
                </div>
              )}
                  
                  {loadingConversations && conversationsPage > 1 && activeTab === 'messages' && (
                    <div className="flex justify-center py-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-gray-200 dark:border-gray-800 border-t-teal-500"></div>
                    </div>
                  )}
            </div>
          </div>
      
            {/* Chat Window */}
          {currentConversation ? (
            <div 
              className="fixed inset-x-0 bottom-0 z-30 md:static md:flex-1 flex flex-col md:h-full bg-white dark:bg-gray-900 overflow-hidden"
              style={{ top: 'calc(4rem + max(var(--safe-area-inset-top, 0px), env(safe-area-inset-top, 0px)) + 15px)' }}
            >
                {/* Chat Header */}
                <div className={`backdrop-blur-md border-b border-gray-100 dark:border-gray-800 p-3 md:p-4 flex-shrink-0 sticky top-0 z-20 ${
                  isCurrentClosed ? 'bg-gradient-to-r from-orange-400/10 to-amber-500/10 dark:from-orange-500/20 dark:to-amber-500/20 border-orange-200/50' : 'bg-white/80 dark:bg-gray-900/80'
                }`}>
                  <div className="flex items-center justify-between max-w-5xl mx-auto w-full">
                    <div className="flex items-center space-x-3">
                      {/* Mobile Back Button */}
                      <button 
                        onClick={() => dispatch(clearMessages())}
                        className="md:hidden p-2 -ml-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-full"
                      >
                        <ArrowLeftIcon className="w-5 h-5" />
                      </button>

                      <Link to={`/profile/${currentConversation.other_user?.id}`} className="relative cursor-pointer group block">
                        {currentConversation.collaboration_request_id ? (
                          <StackedAvatars 
                             avatars={[
                               currentConversation.other_user?.profile_pic_thumb_signed || currentConversation.other_user?.profile_pic || '',
                               currentUser?.profile_pic_thumb_signed || currentUser?.profile_pic || ''
                             ]}
                             names={[
                               `${currentConversation.other_user?.first_name || ''} ${currentConversation.other_user?.last_name || ''}`,
                               `${currentUser?.first_name || ''} ${currentUser?.last_name || ''}`
                             ]}
                             size="md"
                           />
                        ) : (
                          <Avatar 
                            src={currentConversation.other_user?.profile_pic_thumb_signed || currentConversation.other_user?.profile_pic} 
                            alt={`${currentConversation.other_user?.first_name} ${currentConversation.other_user?.last_name}`}
                            size="md"
                            className="w-10 h-10 ring-2 ring-transparent group-hover:ring-gray-200 dark:group-hover:ring-gray-700 transition-all"
                            isDeleted={!!currentConversation.other_user?.deleted_at || !!currentConversation.other_user?.deletion_scheduled_at}
                          />
                        )}
                        {currentConversation.other_user?.is_online && !currentConversation.collaboration_request_id && (
                          <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-500 border-2 border-white dark:border-gray-900 rounded-full"></div>
                        )}
                      </Link>
                      
                      <div className="min-w-0 flex-1">
                        <Link to={`/profile/${currentConversation.other_user?.id}`} className="decoration-gray-400">
                          <h2 className="font-bold text-gray-900 dark:text-white leading-tight flex items-center gap-1.5">
                            <span className="truncate">{currentConversation.subject || `${currentConversation.other_user?.first_name || ''} ${currentConversation.other_user?.last_name || ''}`}</span>
                            {isCurrentClosed && (
                              <span className={collabRequest?.status === 'cancelled'
                                ? "bg-gradient-to-r from-red-500 to-rose-500 text-white text-[10px] px-2 py-0.5 rounded-full font-bold shadow-sm shadow-red-500/20 whitespace-nowrap flex-shrink-0"
                                : "bg-gradient-to-r from-orange-500 to-amber-500 text-white text-[10px] px-2 py-0.5 rounded-full font-bold shadow-sm shadow-orange-500/20 whitespace-nowrap flex-shrink-0"
                              }>
                                {collabRequest?.status === 'cancelled' ? 'Cancelled' : 'Completed'}
                              </span>
                            )}
                          </h2>
                          {currentConversation.subject && (
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {currentConversation.other_user?.first_name} {currentConversation.other_user?.last_name}
                            </p>
                          )}
                        </Link>
                        <div className="flex items-center space-x-2">
                          <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 capitalize">
                             {currentConversation.conversation_id.startsWith('temp-') 
                               ? 'New' 
                               : currentConversation.other_user?.user_type || 'User'
                             }
                          </span>
                          {currentConversation.other_user?.is_online && (
                            <span className="text-xs text-green-600 dark:text-green-400 font-medium flex items-center gap-1">
                                <span className="w-1 h-1 bg-green-600 dark:bg-green-400 rounded-full"></span>
                                Online
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-1">
                      {currentConversation.collaboration_request_id && !currentConversation.is_closed && !currentConversation.closing_at && (
                        <div className="flex items-center space-x-2 mr-2">
                           <Button
                            variant="outline"
                            size="sm"
                            onClick={async () => {
                              if (!currentConversation.collaboration_request_id) return;
                              try {
                                const response = await collaborationAPI.getCollaborationRequest(currentConversation.collaboration_request_id);
                                setRequestToProcess(response.data.data.request);
                                setShowCancellationModal(true);
                              } catch (error) {
                                console.error("Failed to fetch request details", error);
                                setRequestToProcess({ id: currentConversation.collaboration_request_id, subject: currentConversation.subject });
                                setShowCancellationModal(true);
                              }
                            }}
                            className="!text-rose-600 dark:!text-rose-400 !border-rose-200 dark:!border-rose-800/50 hover:!bg-rose-50 dark:hover:!bg-rose-900/20 hover:!border-rose-300 dark:hover:!border-rose-700 !px-3 !py-1 text-xs font-bold"
                          >
                            Cancel
                          </Button>
                          {(() => {
                            if (!collabRequest) {
                              return (
                                <Button
                                  variant="primary"
                                  size="sm"
                                  onClick={async () => {
                                    if (!currentConversation.collaboration_request_id) return;
                                    try {
                                      const response = await collaborationAPI.getCollaborationRequest(currentConversation.collaboration_request_id);
                                      const req = response.data.data.request;
                                      setRequestToProcess(req);
                                      
                                      const isSender = currentUser?.id === req.sender_id;
                                      const isReceiver = currentUser?.id === req.receiver_id;
                                      const otherMarked = isSender ? req.receiver_marked_complete : req.sender_marked_complete;
                                      
                                      if (otherMarked) {
                                        setShowCompleteConfirm(true);
                                      } else {
                                        // First user: immediately complete request in background
                                        await collaborationAPI.completeCollaborationRequest(req.id);
                                        toast.success('You marked collaboration as complete. Awaiting partner confirmation.');
                                        const detailsRes = await collaborationAPI.getCollaborationRequest(req.id);
                                        setCollabRequest(detailsRes.data.data.request);
                                      }
                                    } catch (error) {
                                      console.error("Failed to fetch request details", error);
                                      setRequestToProcess({ id: currentConversation.collaboration_request_id, subject: currentConversation.subject });
                                      setShowCompleteConfirm(true);
                                    }
                                  }}
                                  className="bg-teal-600 hover:bg-teal-700 !px-3 !py-1 text-xs font-bold"
                                >
                                  Complete
                                </Button>
                              );
                            }

                            const isSender = currentUser?.id === collabRequest.sender_id;
                            const isReceiver = currentUser?.id === collabRequest.receiver_id;
                            const hasMarkedComplete = isSender ? collabRequest.sender_marked_complete : isReceiver ? collabRequest.receiver_marked_complete : false;
                            const hasOtherMarkedComplete = isSender ? collabRequest.receiver_marked_complete : isReceiver ? collabRequest.sender_marked_complete : false;

                            if (hasMarkedComplete && collabRequest.status !== 'completed') {
                              return (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={handleRevokeComplete}
                                  className="!text-amber-600 dark:!text-amber-400 !border-amber-200 dark:!border-amber-800/50 hover:!bg-amber-50 dark:hover:!bg-amber-900/20 !px-3 !py-1 text-xs font-bold"
                                >
                                  Revoke Complete
                                </Button>
                              );
                            }

                            return (
                              <Button
                                variant="primary"
                                size="sm"
                                onClick={async () => {
                                  setRequestToProcess(collabRequest);
                                  if (hasOtherMarkedComplete) {
                                    setShowCompleteConfirm(true);
                                  } else {
                                    // First user: immediately complete
                                    try {
                                      await collaborationAPI.completeCollaborationRequest(collabRequest.id);
                                      toast.success('You marked collaboration as complete. Awaiting partner confirmation.');
                                      const detailsRes = await collaborationAPI.getCollaborationRequest(collabRequest.id);
                                      setCollabRequest(detailsRes.data.data.request);
                                    } catch (err) {
                                      toast.error('Failed to complete collaboration');
                                    }
                                  }
                                }}
                                className="bg-teal-600 hover:bg-teal-700 !px-3 !py-1 text-xs font-bold"
                              >
                                {hasOtherMarkedComplete ? 'Confirm Complete' : 'Complete'}
                              </Button>
                            );
                          })()}
                        </div>
                      )}
                      <Menu as="div" className="relative inline-block text-left">
                        <Menu.Button className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors">
                          <EllipsisVerticalIcon className="h-6 w-6" />
                        </Menu.Button>
                        <Transition
                          as={Fragment}
                          enter="transition ease-out duration-100"
                          enterFrom="transform opacity-0 scale-95"
                          enterTo="transform opacity-100 scale-100"
                          leave="transition ease-in duration-75"
                          leaveFrom="transform opacity-100 scale-100"
                          leaveTo="transform opacity-0 scale-95"
                        >
                          <Menu.Items className="absolute right-0 mt-2 w-48 origin-top-right divide-y divide-gray-100 dark:divide-gray-800 rounded-xl bg-white dark:bg-gray-900 shadow-lg ring-1 ring-black/5 dark:ring-white/10 focus:outline-none z-50 overflow-hidden">
                            <div className="p-1">
                              <Menu.Item>
                                {({ active }) => (
                                  <button
                                    onClick={() => setIsReportModalOpen(true)}
                                    className={`${
                                      active ? 'bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white' : 'text-gray-700 dark:text-gray-300'
                                    } group flex w-full items-center rounded-lg px-3 py-2 text-sm font-medium transition-colors`}
                                  >
                                    Report User
                                  </button>
                                )}
                              </Menu.Item>
                              <Menu.Item>
                                {({ active }) => {
                                  const isBlocked = currentConversation.is_blocked_by_me || 
                                    (currentConversation.other_user?.id && blockedUserIds.has(currentConversation.other_user.id));
                                  
                                  return (
                                    <button
                                      onClick={() => {
                                        if (isBlocked) {
                                          setShowUnblockModal(true);
                                        } else {
                                          setShowBlockModal(true);
                                        }
                                      }}
                                      className={`${
                                        active ? 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400' : 'text-red-600 dark:text-red-500'
                                      } group flex w-full items-center rounded-lg px-3 py-2 text-sm font-medium transition-colors`}
                                    >
                                      {isBlocked ? 'Unblock User' : 'Block User'}
                                    </button>
                                  );
                                }}
                              </Menu.Item>
                            </div>
                            <div className="p-1">
                              <Menu.Item>
                                {({ active }) => (
                                  <button
                                    onClick={() => setShowDeleteModal(true)}
                                    className={`${
                                      active ? 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400' : 'text-red-600 dark:text-red-500'
                                    } group flex w-full items-center rounded-lg px-3 py-2 text-sm font-medium transition-colors`}
                                  >
                                    Delete Conversation
                                  </button>
                                )}
                              </Menu.Item>
                            </div>
                          </Menu.Items>
                        </Transition>
                      </Menu>
                    </div>
                  </div>
                </div>
      
                {/* Expiration Banner */}
                {timeLeft && !currentConversation.is_closed && (
                  <div className="bg-amber-50 dark:bg-amber-900/20 px-4 py-2 border-b border-amber-100 dark:border-amber-800/50 flex items-center justify-between">
                    <div className="flex items-start text-amber-800 dark:text-amber-200 text-xs font-medium min-w-0">
                      <ClockIcon className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
                      <span className="break-words">Chat expires in {timeLeft}. History will remain visible, but no new messages can be sent.</span>
                    </div>
                  </div>
                )}

                {currentConversation.is_closed && (
                   <div className="bg-gray-50 dark:bg-gray-800/50 px-4 py-2 border-b border-gray-200 dark:border-gray-700 flex items-center justify-center">
                    <div className="flex items-center text-gray-600 dark:text-gray-400 text-xs font-medium">
                      <XCircleIcon className="w-4 h-4 mr-2" />
                      This collaboration has ended. Chat is now in read-only mode.
                    </div>
                  </div>
                )}

                {/* CRM Progress/Updates Banner */}
                {crmLeadDetails && (
                  <div className="bg-indigo-50/50 dark:bg-indigo-950/20 px-4 py-3 border-b border-indigo-100 dark:border-indigo-900/50 flex flex-col md:flex-row md:items-center justify-between gap-3">
                    <div className="flex items-center space-x-2">
                      <span className="w-2 h-2 rounded-full bg-indigo-500 animate-pulse" />
                      <span className="text-xs font-bold text-indigo-950 dark:text-indigo-200 uppercase tracking-widest">
                        Shoot Proposal: {crmLeadDetails.interest_type || 'Custom Shoot'}
                      </span>
                    </div>
                    
                    {/* Stepper */}
                    <div className="flex items-center space-x-2 md:space-x-4">
                      {[
                        { label: 'New', active: true },
                        { label: 'Proposal Sent', active: crmLeadDetails.status !== 'new' && crmLeadDetails.status !== 'contacted' },
                        { 
                          label: crmLeadDetails.status === 'declined' ? 'Declined' : 'Confirmed', 
                          active: crmLeadDetails.status === 'confirmed' || crmLeadDetails.status === 'declined' || crmLeadDetails.status === 'converted',
                          isEnd: true,
                          isDeclined: crmLeadDetails.status === 'declined'
                        }
                      ].map((step, idx) => (
                        <React.Fragment key={step.label}>
                          {idx > 0 && (
                            <div className={`h-0.5 w-4 md:w-8 rounded-full ${step.active ? (step.isDeclined ? 'bg-red-350 dark:bg-red-800' : 'bg-teal-500') : 'bg-gray-200 dark:bg-gray-700'}`} />
                          )}
                          <span className={`text-[10px] md:text-xs font-black uppercase tracking-wider px-2 py-0.5 rounded-full ${
                            step.active 
                              ? (step.isDeclined ? 'bg-red-100 text-red-705 dark:bg-red-900/20 dark:text-red-400' : 'bg-teal-500/10 text-teal-600 dark:text-teal-400') 
                              : 'bg-gray-105 text-gray-400 dark:bg-gray-800 dark:text-gray-500'
                          }`}>
                            {step.label}
                          </span>
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                )}

                {crmProjectDetails && (
                  <div className="bg-teal-50/50 dark:bg-teal-950/20 px-4 py-3 border-b border-teal-100 dark:border-teal-900/50 flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex flex-col md:flex-row md:items-center gap-3">
                      <div className="flex items-center space-x-2">
                        <span className="w-2 h-2 rounded-full bg-teal-505 animate-pulse" />
                        <span className="text-xs font-black text-teal-950 dark:text-teal-200 uppercase tracking-widest">
                          Project: {crmProjectDetails.title}
                        </span>
                      </div>
                      
                      {/* Stepper */}
                      <div className="flex items-center space-x-2 md:space-x-4">
                        {['booked', 'shooting', 'editing', 'completed'].map((status, idx) => {
                          const projectStatuses = ['booked', 'shooting', 'editing', 'completed'];
                          const currentIdx = projectStatuses.indexOf(crmProjectDetails.status);
                          const active = projectStatuses.indexOf(status) <= currentIdx;
                          return (
                            <React.Fragment key={status}>
                              {idx > 0 && (
                                <div className={`h-0.5 w-3 md:w-6 rounded-full ${active ? 'bg-teal-500' : 'bg-gray-200 dark:bg-gray-700'}`} />
                              )}
                              <span className={`text-[9px] md:text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full ${
                                active 
                                  ? 'bg-teal-500/10 text-teal-600 dark:text-teal-400' 
                                  : 'bg-gray-105 text-gray-400 dark:bg-gray-800 dark:text-gray-500'
                              }`}>
                                {status}
                              </span>
                            </React.Fragment>
                          );
                        })}
                      </div>
                    </div>

                    {/* Quick Access Links */}
                    <div className="flex items-center gap-2">
                      {crmProjectDetails.gallery_status === 'published' && (
                        <button
                          onClick={() => navigate(`/public/gallery/${crmProjectDetails.id}`)}
                          className="px-2.5 py-1 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-750 text-[10px] font-bold uppercase tracking-wider rounded-lg text-teal-600 dark:text-teal-400 flex items-center transition-colors cursor-pointer"
                        >
                          📸 Proof Gallery
                        </button>
                      )}
                      <button
                        onClick={() => navigate('/crm', { state: { activeTab: 'projects', openProject: crmProjectDetails.id } })}
                        className="px-2.5 py-1 bg-teal-600 hover:bg-teal-700 text-white text-[10px] font-bold uppercase tracking-wider rounded-lg flex items-center transition-colors cursor-pointer"
                      >
                        Details
                      </button>
                    </div>
                  </div>
                )}

                {/* Messages Area */}
                <div 
                  ref={messagesContainerRef}
                  onScroll={handleScroll}
                  className="flex-1 overflow-y-auto px-3 py-4 md:p-6 pb-24 space-y-6 scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-700 scrollbar-track-transparent"
                >
                  {/* Pending Request Banner */}
                  {currentConversation.status === 'pending' && currentConversation.requester_id !== currentUser?.id && (
                    <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800/50 p-4 rounded-lg mb-4 text-center">
                      <p className="text-yellow-800 dark:text-yellow-200 mb-2 font-medium">
                        {currentConversation.other_user.first_name} wants to send you a message.
                      </p>
                      <div className="flex justify-center space-x-3">
                        <button
                          onClick={handleAcceptRequest}
                          className="px-4 py-1.5 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 transition-colors"
                        >
                          Accept
                        </button>
                        <button
                          onClick={handleRejectRequest}
                          className="px-4 py-1.5 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                        >
                          Deny
                        </button>
                      </div>
                    </div>
                  )}

                  {currentConversation.status === 'pending' && currentConversation.requester_id === currentUser?.id && (
                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800/50 p-3 rounded-lg mb-4 text-center">
                      <p className="text-blue-800 dark:text-blue-200 text-sm">
                        Message request sent. You can send more messages once they accept.
                      </p>
                    </div>
                  )}

                  {loadingMessages && currentPage === 1 ? (
                    <div className="flex items-center justify-center h-full">
                      <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-200 dark:border-gray-800 border-t-teal-500"></div>
                    </div>
                  ) : (
                    <>
                      {loadingMessages && currentPage > 1 && (
                        <div className="flex justify-center py-2">
                          <div className="animate-spin rounded-full h-5 w-5 border-2 border-gray-200 dark:border-gray-800 border-t-teal-500"></div>
                        </div>
                      )}

                      {messages.map((message, index) => {
                        const isOwnMessage = message.sender_id === currentUser?.id;
                        const showAvatar = !isOwnMessage && (index === 0 || messages[index - 1].sender_id !== message.sender_id);
                        
                        // Unread Divider
                        const showUnreadDivider = message.id === unreadDividerId;

                        if (message.message_type === 'system') {
                          return (
                            <React.Fragment key={message.id}>
                              {showUnreadDivider && (
                                <div className="flex items-center justify-center my-6">
                                  <div className="h-px bg-red-200 dark:bg-red-900/50 flex-1"></div>
                                  <span className="px-3 text-xs font-medium text-red-500 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded-full py-0.5">Unread Messages</span>
                                  <div className="h-px bg-red-200 dark:bg-red-900/50 flex-1"></div>
                                </div>
                              )}
                              <div className="flex justify-center my-4">
                                <div className="bg-gray-50/80 dark:bg-gray-800/80 border border-gray-100 dark:border-gray-700 rounded-2xl px-5 py-3 max-w-[85%] text-center shadow-sm">
                                  <p className="text-[10px] font-bold text-teal-600 dark:text-teal-400 uppercase tracking-widest mb-1.5 flex items-center justify-center gap-1">
                                    <span className="w-1.5 h-1.5 bg-teal-500 rounded-full"></span>
                                    System Update
                                  </p>
                                  <p className="text-xs md:text-sm text-gray-700 dark:text-gray-300 font-medium leading-relaxed">
                                    {renderTextWithLinks(message.content, false)}
                                  </p>
                                  <p className="text-[9px] text-gray-400 dark:text-gray-500 mt-1 uppercase tracking-wider font-semibold">
                                    {formatMessageTime(message.created_at)}
                                  </p>
                                </div>
                              </div>
                            </React.Fragment>
                          );
                        }

                        // Parse content if it's a special type or looks like JSON
                        let contentData: any = null;
                        const isJsonLike = message.content && typeof message.content === 'string' && message.content.trim().startsWith('{') && message.content.trim().endsWith('}');
                        
                        if (['profile', 'portfolio', 'collaboration', 'opportunity'].includes(message.message_type) || isJsonLike) {
                            try {
                                const parsed = JSON.parse(message.content);
                                // Verify it has the expected structure for our cards
                                if (parsed && (parsed.type || parsed.title || parsed.image)) {
                                    contentData = parsed;
                                    // If we successfully parsed it and it has a type, use that type for rendering logic if needed
                                    if (!['profile', 'portfolio', 'collaboration', 'opportunity'].includes(message.message_type) && parsed.type) {
                                        // We can treat it as the parsed type
                                        message = { ...message, message_type: parsed.type };
                                    }
                                }
                            } catch (e) {
                                // Not valid JSON, treat as text
                            }
                        }

                        console.log("Msg render details:", { id: message.id, type: message.message_type, content: message.content, contentData });

                        return (
                          <React.Fragment key={message.id}>
                            {showUnreadDivider && (
                              <div className="flex items-center justify-center my-6">
                                <div className="h-px bg-red-200 dark:bg-red-900/50 flex-1"></div>
                                <span className="px-3 text-xs font-medium text-red-500 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded-full py-0.5">Unread Messages</span>
                                <div className="h-px bg-red-200 dark:bg-red-900/50 flex-1"></div>
                              </div>
                            )}
                            
                            <motion.div 
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                              className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'} group`}
                            >
                              {!isOwnMessage && (
                                <div className={`flex-shrink-0 mr-3 w-8 ${showAvatar ? 'opacity-100' : 'opacity-0'}`}>
                                  <Avatar 
                                    src={currentConversation.other_user?.profile_pic_thumb_signed || currentConversation.other_user?.profile_pic} 
                                    alt={currentConversation.other_user?.first_name}
                                    size="sm"
                                  />
                                </div>
                              )}
                              
                              <div className={`max-w-[75%] md:max-w-[60%] space-y-1 ${isOwnMessage ? 'items-end flex flex-col' : 'items-start flex flex-col'}`}>
                                {/* Render Content Based on Type */}
                                    {contentData ? (
                                        <div 
                                            onClick={() => handleItemClick(contentData?.type || message.message_type, contentData)}
                                            className={`block overflow-hidden rounded-xl border transition-all hover:shadow-lg hover:-translate-y-0.5 text-left bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 group/card cursor-pointer`}
                                            style={{ width: '280px' }}
                                        >
                                            {/* Image Preview */}
                                            {(contentData.image || contentData.cover_image) ? (
                                                <div className="h-36 w-full overflow-hidden bg-gray-100 dark:bg-gray-700 relative">
                                                    <img 
                                                        src={contentData.image || contentData.cover_image} 
                                                        alt={contentData.title} 
                                                        className="h-full w-full object-cover transition-transform duration-500 group-hover/card:scale-105"
                                                    />
                                                    <div className="absolute top-2 left-2 bg-black/60 backdrop-blur-md text-white text-[10px] font-bold px-2 py-1 rounded-md uppercase tracking-wider shadow-sm">
                                                        {message.message_type}
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="h-2 bg-gradient-to-r from-teal-500 to-emerald-500" />
                                            )}
                                            
                                            {/* Text Content */}
                                            <div className="p-3.5">
                                                {!(contentData.image || contentData.cover_image) && (
                                                    <p className="text-[10px] font-bold text-teal-600 dark:text-teal-400 uppercase tracking-wider mb-1.5 flex items-center gap-1">
                                                        <span className="w-1.5 h-1.5 rounded-full bg-teal-500"></span>
                                                        {message.message_type === 'project_card' ? 'Confirmed Booking' : message.message_type}
                                                    </p>
                                                )}
                                                
                                                <h4 className="font-bold text-gray-900 dark:text-white text-sm line-clamp-2 mb-1.5 leading-snug group-hover/card:text-teal-600 dark:group-hover:text-teal-400 transition-colors">
                                                    {contentData.title}
                                                </h4>
                                                
                                                {/* Subtitle / Description */}
                                                {contentData.subtitle && (
                                                    <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2 mb-2.5">
                                                        {contentData.subtitle}
                                                    </p>
                                                )}

                                                {/* Specific Fields based on Type */}
                                                <div className="space-y-1.5">
                                                    {/* Opportunity Details */}
                                                    {message.message_type === 'opportunity' && (
                                                        <>
                                                            {contentData.location && (
                                                                <div className="flex items-center text-xs text-gray-500 dark:text-gray-400">
                                                                    <MapPinIcon className="h-3 w-3 mr-1 flex-shrink-0" />
                                                                    <span className="truncate">{contentData.location}</span>
                                                                </div>
                                                            )}
                                                            <div className="flex items-center gap-2 text-xs">
                                                              {contentData.compensation_type === 'paid' ? (
                                                                  <span className="bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 px-1.5 py-0.5 rounded border border-emerald-100 dark:border-emerald-800/50 font-medium">
                                                                      Paid {contentData.budget ? `• ${formatCurrency(contentData.budget)}` : ''}
                                                                  </span>
                                                              ) : (
                                                                  <span className="bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 px-1.5 py-0.5 rounded border border-gray-200 dark:border-gray-600 font-medium capitalize">
                                                                      {contentData.compensation_type === 'tfp' ? 'TFP' : 
                                                                       contentData.compensation_type === 'negotiable' ? 'Negotiable' : 
                                                                       'Unpaid'}
                                                                  </span>
                                                              )}
                                                            </div>
                                                        </>
                                                    )}

                                                    {/* Collaboration Details */}
                                                    {message.message_type === 'collaboration' && (
                                                        <div className="space-y-2">
                                                            <div className="flex items-center gap-2">
                                                                {contentData.status && (
                                                                    <span className={`text-[10px] px-1.5 py-0.5 rounded border font-medium uppercase tracking-wide ${
                                                                        contentData.status === 'open' ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 border-emerald-100 dark:border-emerald-800/50' : 
                                                                        'bg-gray-50 dark:bg-gray-700 text-gray-600 dark:text-gray-300 border-gray-100 dark:border-gray-600'
                                                                    }`}>
                                                                        {contentData.status}
                                                                    </span>
                                                                )}
                                                            </div>
                                                            {contentData.place && (
                                                                 <div className="flex items-center text-xs text-gray-500 dark:text-gray-400">
                                                                    <MapPinIcon className="h-3 w-3 mr-1 flex-shrink-0" />
                                                                    <span className="truncate">{contentData.place}</span>
                                                                </div>
                                                            )}
                                                            {/* Collaborators */}
                                                            {contentData.collaborators && contentData.collaborators.length > 0 && (
                                                                <div className="flex items-center -space-x-1.5 pt-1">
                                                                    {contentData.collaborators.map((c: any) => (
                                                                        <Avatar 
                                                                            key={c.id} 
                                                                            src={c.profile_pic_thumb_signed || c.profile_pic} 
                                                                            alt={c.name} 
                                                                            size="xs" 
                                                                            className="ring-2 ring-white dark:ring-gray-800 w-5 h-5"
                                                                        />
                                                                    ))}
                                                                    {contentData.collaborator_count > 3 && (
                                                                        <div className="w-5 h-5 rounded-full bg-gray-100 dark:bg-gray-700 ring-2 ring-white dark:ring-gray-800 flex items-center justify-center text-[8px] font-medium text-gray-500 dark:text-gray-400">
                                                                            +{contentData.collaborator_count - 3}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}

                                                    {/* Project Card Details */}
                                                    {message.message_type === 'project_card' && (
                                                        <div className="space-y-3 mt-3 pt-3 border-t border-gray-100 dark:border-gray-700/50">
                                                            <div className="grid grid-cols-1 gap-2">
                                                                {contentData.shoot_date && (
                                                                    <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
                                                                        <CalendarDaysIcon className="h-4 w-4 text-teal-500 flex-shrink-0" />
                                                                        <span>{contentData.shoot_date}</span>
                                                                    </div>
                                                                )}
                                                                {contentData.location && (
                                                                    <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
                                                                        <MapPinIcon className="h-4 w-4 text-teal-500 flex-shrink-0" />
                                                                        <span className="truncate">{contentData.location}</span>
                                                                    </div>
                                                                )}
                                                                {contentData.price && (
                                                                    <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
                                                                        <BanknotesIcon className="h-4 w-4 text-teal-500 flex-shrink-0" />
                                                                        <span className="font-bold text-teal-600 dark:text-teal-400">{contentData.price}</span>
                                                                    </div>
                                                                )}
                                                            </div>
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleItemClick(contentData?.type || message.message_type, contentData);
                                                                }}
                                                                className="w-full mt-1.5 py-2 px-4 bg-teal-600 hover:bg-teal-700 text-white rounded-xl text-xs font-bold active:scale-[0.98] transition-all flex items-center justify-center gap-1.5 shadow-sm"
                                                            >
                                                                View details
                                                                <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                ) : (
                                    <div 
                                    className={`px-4 py-2.5 rounded-2xl text-[15px] leading-relaxed shadow-sm break-words ${
                                        isOwnMessage 
                                        ? 'bg-teal-600 text-white rounded-br-none' 
                                        : 'bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 text-gray-800 dark:text-gray-200 rounded-bl-none'
                                    } whitespace-pre-wrap`}
                                    >
                                    {renderTextWithLinks(message.content, isOwnMessage)}
                                    </div>
                                )}
                                
                                {/* Timestamp & Read Status */}
                                 <div className={`flex items-center space-x-1 text-[10px] ${isOwnMessage ? 'text-gray-400 dark:text-gray-500' : 'text-gray-400 dark:text-gray-500'}`}>
                                   <span>{formatMessageTime(message.created_at)}</span>
                                   {isOwnMessage && (
                                     <span>
                                       {message.is_read ? (
                                         <span className="text-teal-500 dark:text-teal-400 font-medium">Read</span>
                                       ) : (
                                        <span>Sent</span>
                                      )}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </motion.div>
                          </React.Fragment>
                        );
                      })}
                      <div ref={messagesEndRef} />
                    </>
                  )}
                </div>

                {/* Message Input */}
                <div className="p-3 md:p-4 pb-safe bg-white dark:bg-gray-900 border-t border-gray-100 dark:border-gray-800">
                  {!isCurrentExpired ? (
                    <form 
                      onSubmit={sendMessageHandler}
                      className="flex items-end space-x-2 max-w-5xl mx-auto relative"
                    >
                      <div className="flex-1 bg-gray-50 dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 transition-all flex items-end">
                         <div className="relative">
                           <button
                             type="button"
                             onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                             className="p-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                           >
                            <FaceSmileIcon className="h-6 w-6" />
                          </button>
                          {showEmojiPicker && (
                            <div className="absolute bottom-12 left-0 z-50 shadow-xl rounded-2xl overflow-hidden border border-gray-100 dark:border-gray-800">
                              <div 
                                className="fixed inset-0 z-40" 
                                onClick={() => setShowEmojiPicker(false)}
                              ></div>
                              <div className="relative z-50">
                                <EmojiPicker 
                                  onEmojiClick={onEmojiClick}
                                  width={300}
                                  height={400}
                                  theme={document.documentElement.classList.contains('dark') ? 'dark' as any : 'light' as any}
                                />
                              </div>
                            </div>
                          )}
                        </div>
                        
                        <TextareaAutosize
                          ref={messageInputRef}
                          minRows={1}
                          maxRows={5}
                          placeholder={currentConversation?.is_blocked_by_me || currentConversation?.is_blocked_by_them ? "Messaging unavailable" : "Type a message..."}
                          value={newMessage}
                          onChange={(e) => setNewMessage(e.target.value)}
                          onKeyDown={handleKeyPress}
                          className="flex-1 bg-transparent border-none focus:ring-0 focus:outline-none resize-none py-3 px-2 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 leading-relaxed max-h-32"
                        />
                      </div>
                      
                      <button
                        type="submit"
                        disabled={!newMessage.trim() || isSending || currentConversation?.is_blocked_by_me || currentConversation?.is_blocked_by_them}
                        className="p-3 bg-teal-600 text-white rounded-full hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm hover:shadow-md active:scale-95 flex-shrink-0"
                      >
                        {isSending ? (
                          <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        ) : (
                          <PaperAirplaneIcon className="h-6 w-6 -rotate-45 mb-0.5 ml-0.5" />
                        )}
                      </button>
                    </form>
                  ) : (
                    <div className="flex items-center justify-center p-4 bg-gray-50 dark:bg-gray-800/50 rounded-2xl border border-gray-200 dark:border-gray-700 text-sm text-gray-500 max-w-5xl mx-auto font-medium">
                      <LockClosedIcon className="h-4 w-4 mr-2 text-gray-400" />
                      This conversation is closed and view-only.
                    </div>
                  )}
                </div>
            </div>
          ) : (
            /* Empty State */
            <div className="hidden md:flex flex-1 flex-col items-center justify-center bg-gray-50 dark:bg-gray-900/50 text-center p-8">
              <div className="w-24 h-24 bg-white dark:bg-gray-800 rounded-full shadow-sm flex items-center justify-center mb-6">
                <ChatBubbleLeftIcon className="h-12 w-12 text-teal-500" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Your Messages</h2>
              <p className="text-gray-500 dark:text-gray-400 max-w-md">
                Select a conversation from the list to start chatting or connect with new creators.
              </p>
            </div>
          )}

          {/* Modals */}
          <ConfirmationModal
            isOpen={showBlockModal}
            onClose={() => setShowBlockModal(false)}
            onConfirm={handleBlockUser}
            title="Block User"
            message={`Are you sure you want to block ${currentConversation?.other_user?.first_name}? They won't be able to message you.`}
            confirmText="Block"
            type="danger"
            loading={blockActionLoading}
          />

          <ConfirmationModal
            isOpen={showUnblockModal}
            onClose={() => setShowUnblockModal(false)}
            onConfirm={handleUnblockUser}
            title="Unblock User"
            message={`Are you sure you want to unblock ${currentConversation?.other_user?.first_name}?`}
            confirmText="Unblock"
            type="info"
            loading={blockActionLoading}
          />

          {/* Report Modal */}
          <Transition appear show={isReportModalOpen} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={() => setIsReportModalOpen(false)}>
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <div className="fixed inset-0 bg-black/25 backdrop-blur-sm" />
              </Transition.Child>

              <div className="fixed inset-0 overflow-y-auto">
                <div className="flex min-h-full items-center justify-center p-4 text-center">
                  <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0 scale-95"
                    enterTo="opacity-100 scale-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100 scale-100"
                    leaveTo="opacity-0 scale-95"
                  >
                    <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white dark:bg-gray-900 p-6 text-left align-middle shadow-xl transition-all border border-gray-100 dark:border-gray-800">
                      <Dialog.Title
                        as="h3"
                        className="text-lg font-medium leading-6 text-gray-900 dark:text-white mb-4"
                      >
                        Report User
                      </Dialog.Title>
                      
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Reason</label>
                          <select
                            value={reportReason}
                            onChange={(e) => setReportReason(e.target.value)}
                            className="w-full rounded-lg border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:border-teal-500 focus:ring-teal-500"
                          >
                            <option value="">Select a reason</option>
                            <option value="spam">Spam</option>
                            <option value="harassment">Harassment</option>
                            <option value="inappropriate">Inappropriate Content</option>
                            <option value="other">Other</option>
                          </select>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
                          <textarea
                            value={reportDescription}
                            onChange={(e) => setReportDescription(e.target.value)}
                            rows={4}
                            className="w-full rounded-lg border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:border-teal-500 focus:ring-teal-500"
                            placeholder="Please provide more details..."
                          />
                        </div>

                        <div className="flex justify-end space-x-3 mt-6">
                          <button
                            onClick={() => setIsReportModalOpen(false)}
                            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={handleReportUser}
                            disabled={!reportReason || isSubmittingReport}
                            className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50"
                          >
                            {isSubmittingReport ? 'Submitting...' : 'Submit Report'}
                          </button>
                        </div>
                      </div>
                    </Dialog.Panel>
                  </Transition.Child>
                </div>
              </div>
            </Dialog>
          </Transition>
          {/* Item View Modals */}
          {viewingCollaboration && (
            <CollaborationDetailModal
              collaboration={viewingCollaboration}
              onClose={() => setViewingCollaboration(null)}
            />
          )}

           {/* Loading Overlay */}
          {loadingItem && (
            <div className="fixed inset-0 bg-black/20 dark:bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center">
                <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-lg flex items-center gap-3 border border-gray-100 dark:border-gray-700">
                    <div className="w-6 h-6 border-2 border-teal-500 border-t-transparent rounded-full animate-spin" />
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Loading details...</span>
                </div>
            </div>
          )}
          {/* Lifecycle Modals */}
          <CollaborationCompletionModal
            isOpen={showCompletionModal}
            onClose={() => setShowCompletionModal(false)}
            onPostNow={() => {
              setShowCompletionModal(false);
              setShowCreateCollabModal(true);
            }}
            onPostLater={async () => {
              if (!requestToProcess) return;
              try {
                const res = await collaborationAPI.completeCollaborationRequest(requestToProcess.id);
                const isBothCompleted = res.data.data?.both_completed;
                const closingAt = res.data.data?.closing_at;

                if (isBothCompleted) {
                  toast.success('Collaboration marked as complete by both parties! Chat will close in 7 days.');
                  if (currentConversation) {
                    dispatch(updateConversation({
                      conversation_id: currentConversation.conversation_id,
                      closing_at: closingAt || new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString()
                    }));
                  }
                } else {
                  toast.success('You marked collaboration as complete. Awaiting partner confirmation.');
                }
                
                // Re-fetch request details
                const detailsRes = await collaborationAPI.getCollaborationRequest(requestToProcess.id);
                setCollabRequest(detailsRes.data.data.request);
                
                setShowCompletionModal(false);
              } catch (error) {
                toast.error('Failed to complete collaboration');
              }
            }}
          />

          <CancellationReasonModal
            isOpen={showCancellationModal}
            onClose={() => setShowCancellationModal(false)}
            onConfirm={async (reason, details) => {
              if (!requestToProcess) return;
              try {
                await collaborationAPI.cancelCollaborationRequest(requestToProcess.id, { reason, details });
                toast.success('Collaboration cancelled');
                setShowCancellationModal(false);
                if (currentConversation) {
                  dispatch(updateConversation({
                    conversation_id: currentConversation.conversation_id,
                    is_closed: true
                  }));
                }
              } catch (error) {
                toast.error('Failed to cancel collaboration');
              }
            }}
          />

          {showCreateCollabModal && requestToProcess && (
            <CreateCollaborationModal
              collaborationRequest={requestToProcess}
              onClose={() => setShowCreateCollabModal(false)}
              onSuccess={async () => {
                try {
                  const res = await collaborationAPI.completeCollaborationRequest(requestToProcess.id);
                  const isBothCompleted = res.data.data?.both_completed;
                  const closingAt = res.data.data?.closing_at;

                  if (isBothCompleted) {
                    toast.success('Collaboration marked as complete by both parties! Chat will close in 7 days.');
                    if (currentConversation) {
                      dispatch(updateConversation({
                        conversation_id: currentConversation.conversation_id,
                        closing_at: closingAt || new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString()
                      }));
                    }
                  } else {
                    toast.success('You marked collaboration as complete. Awaiting partner confirmation.');
                  }

                  // Re-fetch request details
                  const detailsRes = await collaborationAPI.getCollaborationRequest(requestToProcess.id);
                  setCollabRequest(detailsRes.data.data.request);

                  setShowCreateCollabModal(false);
                } catch (error) {
                  console.error("Failed to mark as complete after post", error);
                  toast.error('Failed to complete collaboration');
                }
              }}
            />
          )}

          {/* Complete Collaboration Confirmation Modal */}
          <ConfirmationModal
            isOpen={showCompleteConfirm}
            onClose={() => setShowCompleteConfirm(false)}
            onConfirm={() => {
              setShowCompleteConfirm(false);
              setShowCompletionModal(true);
            }}
            title="Confirm Completion"
            message="Mark this collaboration as complete? This action cannot be undone. Once both parties complete, the chat will remain open for 7 days before becoming read-only."
            confirmText="Yes, Complete"
            cancelText="Cancel"
            type="danger"
            loading={false}
          />

          {/* Delete Conversation Confirmation Modal */}
          <ConfirmationModal
            isOpen={showDeleteModal}
            onClose={() => setShowDeleteModal(false)}
            onConfirm={handleDeleteConversation}
            title="Delete Conversation"
            message="Are you sure you want to delete this conversation? All messages will be permanently removed. This action cannot be undone."
            confirmText="Delete"
            type="danger"
          />
        </div>
    );
};

export default MessagesPage;
